import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Shared UI-kit Button (PLAN.md M7 design system). Wraps the `.btn` primitives from global.css:
 *  - `primary` → the ONE strong accent fill (pine-teal) for the page's main action (Run / Save plan)
 *  - `default` → hairline-bordered surface button (secondary actions)
 *  - `ghost`   → transparent (toolbar / icon actions like the theme toggle)
 *
 * A typed wrapper so every screen's buttons read identically and inherit `type="button"` (avoiding
 * accidental form submits). Forwards all native button attrs; never hard-codes a color.
 */
export type ButtonVariant = 'primary' | 'default' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Optional leading glyph/icon node rendered before the label. */
  icon?: ReactNode;
  /** Show a subtle busy state (disables + dims) — for async actions like Run / Save. */
  busy?: boolean;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn btn--primary',
  default: 'btn',
  ghost: 'btn btn--ghost',
};

export function Button({
  variant = 'default',
  icon,
  busy = false,
  children,
  className,
  disabled,
  type,
  ...rest
}: ButtonProps): React.ReactElement {
  const classes = `${VARIANT_CLASS[variant]}${busy ? ' btn--busy' : ''}${
    className ? ` ${className}` : ''
  }`;
  return (
    <button
      // Default to a non-submit button; callers pass type="submit" explicitly when needed.
      type={type ?? 'button'}
      className={classes}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon !== undefined && (
        <span aria-hidden="true" style={{ display: 'inline-flex' }}>
          {icon}
        </span>
      )}
      {children}
    </button>
  );
}
